/**
 * @file Bandeaux plein ecran courts : "Stage Clear!" + confettis CSS, "Temps ecoule !", Victoire/Defaite de mode, "Pret... Go !".
 */
import type { RewardBundle } from '@config/types';
import { formatBig } from '@shared/format';
import { Component } from '../core/Component';
import { clear, h } from '../core/dom';
import { walletChips } from '../core/widgets';
import type { UiContext } from '../UiContext';

type BannerKind = 'win' | 'fail' | 'ready' | 'go' | 'result-win' | 'result-lose';

interface Banner {
  kind: BannerKind;
  text: string;
  rewards: RewardBundle | null;
  confetti: boolean;
  /** Duree d'affichage (s). */
  seconds: number;
  /** Un bandeau "leger" (Pret / Go) est coupe des qu'un autre arrive. */
  light: boolean;
}

const SECONDS = 1.4;
const LIGHT_SECONDS = 0.9;
const MAX_QUEUE = 3;
const CONFETTI_PIECES = 36;
const CONFETTI_COLORS = ['#ff7b7b', '#ffd25e', '#8ee07a', '#7fd3ff', '#ff8fb8', '#b69cff'];

export class ResultBanner extends Component {
  private text!: HTMLElement;
  private sub!: HTMLElement;
  private card!: HTMLElement;
  private confetti!: HTMLElement;
  private queue: Banner[] = [];
  private current: (Banner & { left: number }) | null = null;
  private flip = false;

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'banner');
  }

  protected build(): void {
    const t = this.ctx.i18n;
    this.text = h('div', { class: 'banner-text outlined-text' });
    this.sub = h('div', { class: 'banner-sub' });
    this.card = h('div', { class: 'banner-card' }, this.text, this.sub);
    this.confetti = h('div', { class: 'confetti' });
    for (let i = 0; i < CONFETTI_PIECES; i++) {
      const piece = h('i');
      piece.style.setProperty('--dx', `${((Math.random() - 0.5) * 120).toFixed(1)}%`);
      piece.style.setProperty('--left', `${(Math.random() * 100).toFixed(1)}%`);
      piece.style.setProperty('--rot', `${Math.round(Math.random() * 720 - 360)}deg`);
      piece.style.setProperty('--delay', `${(Math.random() * 0.35).toFixed(2)}s`);
      piece.style.setProperty('--dur', `${(1.1 + Math.random() * 0.7).toFixed(2)}s`);
      piece.style.background = CONFETTI_COLORS[i % CONFETTI_COLORS.length] ?? '#fff';
      this.confetti.append(piece);
    }
    this.el.append(this.confetti, this.card);

    this.listen('LevelCleared', (e) => this.push({ kind: 'win', text: t.t('hud.stageClear'), rewards: e.reward, confetti: true, seconds: SECONDS, light: false }));
    this.listen('LevelFailed', () => this.push({ kind: 'fail', text: t.t('hud.levelFailed'), rewards: null, confetti: false, seconds: SECONDS, light: false }));
    this.listen('ModePhaseChanged', (e) => {
      if (e.phase === 'intro') this.push({ kind: 'ready', text: t.t('hud.ready'), rewards: null, confetti: false, seconds: LIGHT_SECONDS, light: true });
      else if (e.phase === 'running') this.push({ kind: 'go', text: t.t('hud.go'), rewards: null, confetti: false, seconds: LIGHT_SECONDS, light: true });
    });
    // phases du Boss / du Raid : bandeau court (carapace : pylones a detruire ; sinon numero de phase)
    this.listen('BossPhase', (e) =>
      this.push({ kind: 'ready', text: e.shield ? t.t('hud.bossShieldUp') : t.t('hud.bossPhaseUp', { n: e.phase, max: e.phases }), rewards: null, confetti: false, seconds: SECONDS, light: true }),
    );
    this.listen('ModeExited', (e) => {
      if (!e.result) return;
      this.push({
        kind: e.result.success ? 'result-win' : 'result-lose',
        text: t.t(e.result.success ? 'mode.result.win' : 'mode.result.lose') + (e.result.score > 0 && (e.result.mode === 'boss' || e.result.mode === 'raid') ? ` · ${t.t('mode.result.score', { n: e.result.score })}` : ''),
        rewards: e.result.rewards,
        confetti: e.result.success,
        seconds: SECONDS,
        light: false,
      });
    });
    this.hide();
  }

  private push(b: Banner): void {
    // Un bandeau leger en cours est coupe par le suivant ; les bandeaux legers en attente sont remplaces.
    if (this.current?.light) this.current.left = 0;
    this.queue = this.queue.filter((q) => !q.light);
    this.queue.push(b);
    if (this.queue.length > MAX_QUEUE) this.queue.splice(0, this.queue.length - MAX_QUEUE);
  }

  private show(b: Banner): void {
    this.current = { ...b, left: b.seconds };
    this.flip = !this.flip;
    this.el.className = `banner on kind-${b.kind} ${this.flip ? 'run-a' : 'run-b'}${b.confetti ? ' with-confetti' : ''}`;
    this.text.textContent = b.text;
    clear(this.sub);
    if (b.rewards) {
      const chips = walletChips(this.ctx, b.rewards.wallet);
      if (b.rewards.passXp) chips.push(h('span', { class: 'chip' }, h('b', {}, `+${formatBig(b.rewards.passXp)} XP`)));
      for (const c of chips) this.sub.append(c);
    }
  }

  private hide(): void {
    this.current = null;
    this.el.className = 'banner';
  }

  override update(dt: number): void {
    if (this.current) {
      this.current.left -= dt;
      if (this.current.left > 0) return;
      this.hide();
    }
    const next = this.queue.shift();
    if (next) this.show(next);
  }
}
